import Vue from 'vue'
import { state as msalState } from '@/libs/Auth'
import TicketsService from '@/services/API/TicketsService'

/**
 * Usefull if we need to wait a bit before executing the next statement.
 * @param {number} time delay in miliseconds
 * @returns
 */
const delay = (time) => {
  return new Promise(resolve => setTimeout(resolve, time))
}

export const UsersRoles = {
  CPU_ADMIN: 'CPU_Admin',
  CPU_ADMIN_RESEAU: 'CPU_Admin_réseau',
  CPU_ADMINS_CLIENTS: 'CPU_admins_clients',
  CPU_ADMIN_INTERNE: 'CPU_admin_interne',
  CPU_CHARGE_PROJETS: 'CPU_Chargé_projets',
  CPU_LOGISTIQUE: 'CPU_Logistique',
  CPU_REP_CA: 'CPU_rep_ca',
  CPU_SUPPORT_NIV_1_2_3: 'CPU_Support_Niv_1_2_3',
  CPU_TECHN_ROUTE: 'CPU_Techn_route',
  CPU_TECH: 'CPU_tech',
  DEVOPS: 'devops',
  CPU_SPEC_RESEAU: 'CPU_Spéc_Réseau',
  CPU_TECHN_ROUTE_SB: 'CPU_Techn_route_SB'
}

export class User {
  constructor (data) {
    Object.assign(this, data)
  }

  get initials () {
    const first = this.name.split(' ')[0].charAt(0)
    const last = this.name.split(' ')[1].charAt(0)
    return `${first !== '(' ? first : ''}${last}`
  }

  get uuid () {
    return this?._id
  }

  hasRole (needed) {
    if (!this.roles) {
      return false
    }
    const userRoles = this.roles.filter(code => needed.includes(code))
    return !!userRoles.length
  }
}

export function makeRealtimeLogger (prefix, injectedLog) {
  return function log (...args) {
    if (injectedLog && typeof injectedLog === 'function') {
      try {
        injectedLog(prefix, ...args)
      } catch {
        // ignore
      }
      return
    }

    try {
      // eslint-disable-next-line no-console
      console.log(prefix, ...args)
    } catch {
      // ignore
    }
  }
}

const state = Vue.observable({
  user: null,
  loaded: false,
  error: null,
  loading: false,
  profile: null,
  accountStatus: 'OK'
})

const UserDataProvider = {
  name: 'UserDataProvider',
  props: {
    value: {
      type: Object,
      default: null
    },
    manageLifecycle: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    retry: 0
  }),
  computed: {
    currentState () {
      return state
    },
    account () {
      return this.$auth.account
    },
    companyName () {
      if (!state.user) {
        return undefined
      }
      return state.user.companyName
    },
    isAuthenticated () {
      return this.$auth.isAuthenticated
    },
    userFullName () {
      if (!this.account) {
        return null
      }
      return this.account.name
    },
    accountStatus () {
      return state.accountStatus
    },
    profile () {
      return state.profile
    },
    scopes () {
      if (!state.user) {
        return []
      }
      return state.user.ticketScopes
    }
  },
  watch: {
    isAuthenticated: {
      handler (value) {
        this._debug('isAuthenticated watch', value)
        if (!this.manageLifecycle) {
          return
        }
        if (value) {
          if (state.loading) {
            return
          }
          if (state.loaded) {
            this.$emit('input', state.user)
            this.$emit('loaded', state.user)
            return
          }
          state.loading = true
          this.$nextTick(() => {
            this.getADProfile()
          })
        }
      },
      immediate: true
    },
    profile: {
      handler (value) {
        this._debug('profile watch', value)
        if (!value) {
          return
        }

        if (value.accessType && value.companyName) {
          this.getMongoData()
        }

        if (!value.accessType || !value.companyName) {
          state.accountStatus = 'CREATION_PENDING'
          if (this.retry < 3) {
            delay(15000).then(() => {
              this.retry++
              this.$nextTick(() => {
                this.getADProfile()
              })
            })
          } else {
            if (value.accessType && !value.companyName) {
              state.accountStatus = 'MISSING_INFO'
            } else {
              state.accountStatus = 'ACCESS_UNDEFINED'
            }
            state.loading = false
            state.loaded = true
          }
        }
      },
      immediate: false
    }
  },
  methods: {
    hasAdGroup (targets, groups = []) {
      const intersected = targets.filter(x => groups.includes(x))
      return !!intersected.length
    },
    getAccessType (profile) {
      profile = profile ?? state.profile ?? undefined
      const groups = profile?.accessGroups ?? []
      // Clients portal (Auto created) - 2c567388-172e-453d-a184-7e8c6158a95d
      if (this.hasAdGroup(['2c567388-172e-453d-a184-7e8c6158a95d'], groups)) {
        return 'CLIENT_ACCESS'
      }
      // Partners portal (Auto created) - f7820efb-b7b2-45dd-b048-b0d17c4d7fb9
      if (this.hasAdGroup(['f7820efb-b7b2-45dd-b048-b0d17c4d7fb9'], groups)) {
        return 'PARTNER_ACCESS'
      }
      // CPU - b4fc80c9-5559-43dc-922b-949350623a6a
      if (this.hasAdGroup(['b4fc80c9-5559-43dc-922b-949350623a6a'], groups)) {
        return 'CPU_ACCESS'
      }
      return undefined
    },
    async getMongoData () {
      state.error = null
      state.loaded = false
      state.loading = true
      let user
      let status
      try {
        state.error = null
        state.loaded = false
        state.loading = true

        const payload = {
          username: this.profile.username,
          companyName: this.profile.companyName,
          accessType: this.profile.accessType
        }

        const response = await TicketsService.getContact(payload)
        if ('error' in response) {
          if (response.error === 'Contact not found') {
            status = 'PROFILE_NOT_FOUND'
            user = new User({
              ...response.data,
              ...this.profile
            })
          } else {
            throw new Error(response.error)
          }
        } else {
          status = 'OK'
          switch (response.source) {
            case 'CPU_ACCESS':
              user = new User({
                ...response.data,
                ...this.profile
              })
              break
            case 'PARTNER_ACCESS':
              user = new User({
                ...response.data,
                ...this.profile
              })
              break
            case 'CLIENT_ACCESS':
              user = new User({
                ...response.data,
                ...this.profile
              })
              break
          }
        }
        state.accountStatus = status
        state.user = user
        state.loading = false
        state.loaded = true
      } catch (error) {
        state.error = error
        state.loaded = !state.error
        state.loading = false
        this.$emit('error', error)
      }
    },
    async getADProfile () {
      try {
        state.error = null
        state.loaded = false
        state.loading = true

        const msalResponse = await msalState.instance.acquireTokenSilent({
          scopes: ['https://graph.microsoft.com/.default'],
          account: msalState.account
        })

        const url = 'https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,preferredLanguage,companyName'
        const headers = new Headers()
        headers.append('Content-Type', 'application/json')
        headers.append('Authorization', `Bearer ${msalResponse.accessToken}`)
        const init = {
          method: 'GET',
          headers: headers
        }
        const response = await fetch(url, init)

        if (!response.ok) {
          throw new Error(`MSGraph Error: ${response.status} - ${response.statusText}`)
        }
        const body = await response.json()
        const data = {
          homeAccountId: msalState.account.homeAccountId,
          localAccountId: msalState.account.localAccountId,
          id: body.id,
          username: msalState.account.username,
          name: body.displayName.replace('MAILBOX ARCHIVE CPG - ', ''),
          email: body.mail,
          companyName: body.companyName,
          accessType: undefined,
          lang: body.preferredLanguage ?? msalState.preferredLanguage,
          accessGroups: msalState?.groups || []
        }
        data.accessType = this.getAccessType(data)
        state.profile = data
      } catch (error) {
        state.error = error
        state.loaded = !state.error
        state.loading = false
        this.$emit('error', error)
      }
    },
    _debug (...args) {
      if (!this._logger) {
        this._logger = makeRealtimeLogger('[UserDataProvider]', (this.$root && this.$root.$log) || null)
      }
      if (this._logger) {
        this._logger(...args)
      } else {
        const prefix = '[UserDataProvider]'
        try {
          // eslint-disable-next-line no-console
          console.log(prefix, ...args)
        } catch {
          // ignore
        }
      }
    }
  },
  mounted () {
    this._debug('mounted', { manageLifecycle: this.manageLifecycle })
  },
  beforeDestroy () {
    this._debug('beforeDestroy', { manageLifecycle: this.manageLifecycle })
    // // Avoid tearing down shared realtime unless this instance owns the lifecycle.
    // if (this.manageLifecycle) {
    //   this._stop('destroy')
    // }
  },
  render () {
    if (this.$scopedSlots.default !== undefined) {
      return this.$scopedSlots.default({
        loaded: state.loaded,
        loading: state.loading,
        user: state.user,
        scopes: this.scopes,
        error: state.error,
        isAuthenticated: this.isAuthenticated,
        adGroups: state.profile?.accessGroups,
        companyName: state.profile?.companyName,
        hasAdGroup: this.hasAdGroup,
        userFullName: state.profile?.name,
        accessType: state.profile?.accessType,
        accountStatus: state.accountStatus,
        profile: state.profile
      })
    } else {
      return this.$slots.default
    }
  }
}

export default UserDataProvider
